// /admin/security/issues — High & critical security findings dashboard.
//
// Companion to /admin/security: that page tracks the full list (open/fixed
// status across every severity). This one is laser-focused on what's actually
// dangerous — it shows ONLY findings with impact "critical" or "high", and
// for each one surfaces the table, policy and a copy-pastable SQL fix so an
// admin can resolve the issue in seconds without leaving the page.

import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ShieldAlert,
  ShieldCheck,
  Copy,
  Check,
  ExternalLink,
  Loader2,
  Database,
  Lock,
  ArrowRight,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { setSeo } from "@/lib/seo";
import { toast } from "sonner";
import {
  SECURITY_FINDINGS,
  SEVERITY_LABEL,
  type Finding,
  type FindingImpact,
} from "@/data/securityFindings";

const IMPACT_RANK: Record<FindingImpact, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const IMPACT_CLASS: Record<FindingImpact, string> = {
  critical: "bg-destructive/15 text-destructive border-destructive/40",
  high: "bg-yellow-500/15 text-yellow-500 border-yellow-500/40",
  medium: "bg-muted text-muted-foreground border-border",
  low: "bg-muted text-muted-foreground border-border",
};

const IMPACT_LABEL: Record<FindingImpact, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

const AdminSecurityIssues = () => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    setSeo({
      title: "Security issues — HackerLabs.ai",
      description:
        "High and critical security findings with table, policy, and copy-pastable SQL fixes. Admin-only.",
      url: "https://hackerlabs.ai/admin/security/issues",
    });
  }, []);

  // Only items the team actually needs to act on.
  const highCritical = useMemo<Finding[]>(() => {
    return [...SECURITY_FINDINGS]
      .filter((f) => f.impact === "critical" || f.impact === "high")
      .sort((a, b) => IMPACT_RANK[a.impact] - IMPACT_RANK[b.impact]);
  }, []);

  const counts = useMemo(() => {
    let critical = 0;
    let high = 0;
    for (const f of highCritical) {
      if (f.impact === "critical") critical++;
      else if (f.impact === "high") high++;
    }
    return { critical, high, total: highCritical.length };
  }, [highCritical]);

  const copySql = async (id: string, sql: string) => {
    try {
      await navigator.clipboard.writeText(sql);
      setCopiedId(id);
      toast.success("SQL copied — paste into the migration editor");
      window.setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1800);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  // ---- Auth / role gates ----
  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <PageBreadcrumb
        items={[
          { label: "Admin", to: "/admin/site" },
          { label: "Security", to: "/admin/security" },
          { label: "Issues" },
        ]}
      />
      <main className="container mx-auto px-4 py-8 max-w-4xl flex-1 space-y-6">
        <header className="space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-wider text-primary">
            $ grep -E "critical|high" /admin/security/findings.md
          </p>
          <h1 className="text-2xl font-mono font-extrabold text-foreground flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-destructive" />
            Security issues
          </h1>
          <p className="text-sm text-muted-foreground">
            High and critical findings only, with the affected table, policy
            name, and a one-click SQL fix you can paste into a migration.
          </p>
        </header>

        {/* Status summary */}
        <section className="grid grid-cols-3 gap-3">
          <SummaryCard
            label="Total"
            value={counts.total}
            icon={<ShieldCheck className="w-4 h-4" />}
          />
          <SummaryCard
            label="Critical"
            value={counts.critical}
            tone={counts.critical > 0 ? "danger" : "ok"}
            icon={<ShieldAlert className="w-4 h-4" />}
          />
          <SummaryCard
            label="High"
            value={counts.high}
            tone={counts.high > 0 ? "warn" : "ok"}
            icon={<ShieldAlert className="w-4 h-4" />}
          />
        </section>

        {/* Findings list */}
        {highCritical.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            <ShieldCheck className="w-8 h-8 text-primary mx-auto mb-2" />
            No high or critical issues. Nice work.
          </div>
        ) : (
          <ul className="space-y-3">
            {highCritical.map((f) => {
              const isCopied = copiedId === f.internal_id;
              return (
                <li
                  key={f.internal_id}
                  className="rounded-lg border border-border bg-card p-4 space-y-3"
                >
                  <div className="flex items-start gap-2 flex-wrap">
                    <Badge variant="outline" className={IMPACT_CLASS[f.impact]}>
                      {IMPACT_LABEL[f.impact]}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="border-border text-muted-foreground"
                    >
                      {SEVERITY_LABEL[f.severity]}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="border-border text-muted-foreground"
                    >
                      {f.scanner}
                    </Badge>
                    <span className="ml-auto text-[10px] font-mono text-muted-foreground">
                      +{f.estimated_improvement} pts when resolved
                    </span>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-card-foreground">
                      {f.name}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {f.description}
                    </p>
                  </div>

                  {/* Location: table + policy */}
                  {(f.table || f.policy) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono">
                      {f.table && (
                        <div className="flex items-center gap-2 px-2 py-1.5 rounded border border-border bg-secondary/50">
                          <Database className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <span className="text-muted-foreground shrink-0">
                            table:
                          </span>
                          <span className="text-card-foreground truncate">
                            {f.table}
                          </span>
                        </div>
                      )}
                      {f.policy && (
                        <div className="flex items-center gap-2 px-2 py-1.5 rounded border border-border bg-secondary/50">
                          <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <span className="text-muted-foreground shrink-0">
                            policy:
                          </span>
                          <span className="text-card-foreground truncate">
                            {f.policy}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Copy-pastable SQL fix */}
                  {f.fix_sql ? (
                    <div className="rounded-md border border-border bg-secondary/40 overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-secondary/60">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                          Fix — paste into a new migration
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copySql(f.internal_id, f.fix_sql!)}
                          className="h-6 px-2 text-xs"
                        >
                          {isCopied ? (
                            <>
                              <Check className="w-3 h-3 mr-1" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3 mr-1" />
                              Copy SQL
                            </>
                          )}
                        </Button>
                      </div>
                      <pre className="text-[11px] font-mono text-card-foreground p-3 overflow-x-auto whitespace-pre">
                        {f.fix_sql}
                      </pre>
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed border-border bg-secondary/30 px-3 py-2 text-[11px] font-mono text-muted-foreground">
                      No SQL fix — see the linked docs for remediation steps.
                    </div>
                  )}

                  <div className="flex items-center gap-3 flex-wrap pt-1">
                    <a
                      href={f.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-foreground"
                    >
                      Docs <ExternalLink className="w-3 h-3" />
                    </a>
                    <Link
                      to="/admin/security"
                      className="inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline ml-auto"
                    >
                      Track status <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <p className="text-[11px] font-mono text-muted-foreground pt-2">
          Need to mark something fixed or reopen a finding? Use the{" "}
          <Link to="/admin/security" className="text-primary hover:underline">
            full security settings page
          </Link>
          .
        </p>
      </main>
    </div>
  );
};

const SummaryCard = ({
  label,
  value,
  icon,
  tone = "neutral",
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone?: "neutral" | "warn" | "ok" | "danger";
}) => {
  const toneClass =
    tone === "danger"
      ? "text-destructive"
      : tone === "warn"
      ? "text-yellow-500"
      : tone === "ok"
      ? "text-primary"
      : "text-foreground";
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className={"text-2xl font-mono font-bold mt-1 " + toneClass}>
        {value}
      </div>
    </div>
  );
};

export default AdminSecurityIssues;
